<script setup lang="ts">
import { computed, onMounted, ref, onBeforeUnmount } from "vue";
import { RadioInput, SelectInput, TextArea } from "@/components/inputs";
import { formatPhone } from "@/util/phone";
import { useAwardStore, useCandidatesStore, useTwilioStore } from "@/stores";
import useAwards from "@/composables/use-awards";
import CodeInput from "../CodeInput.vue";
import type { StepOneForm } from "./StepOne.vue";
import type { Candidate } from "best-of-awards-api";

type Props = {
  modelValue: boolean;
  candidate: Candidate;
  phones: Array<string>;
  email: string;
  domainsMatch: boolean;
  formData: StepOneForm;
  onVerified: ({ manual }: { manual: boolean }) => void;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "verified", { manual }: { manual: boolean }): void;
}>();

const dialogState = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit("update:modelValue", value);
  },
});

onMounted(() => {
  if (props.domainsMatch) {
    verifySmsOrCallOrEmail("email");
  }
});

const awardStore = useAwardStore();
const candidatesStore = useCandidatesStore();
const twilioStore = useTwilioStore();
const { awardYear, afterCutoff } = useAwards();

const selectedPhone = ref(props.phones[0] ?? "");
const codeSent = ref(false);
const code = ref("");
const errorMsg = ref("");
const verifiedPhones = ref<Array<string>>([]);
const waitingForInboundCall = ref(false);
type Channel = "" | "sms" | "email" | "call" | "inbound";
const channel = ref<Channel>("");
const sending = ref(false);
const verifying = ref(false);
const refreshIntervalId = ref(-1);
const showManualClaimForm = ref(false);
const verificationIssue = ref("");
const verificationIssueOther = ref("");
const verificationIssueMissing = ref(false);
const verificationIssueOtherMissing = ref(false);
const submittingManualClaim = ref(false);
const showNotWorking = ref(false);

const verifyPhone = computed(() => {
  const { smsNumber } = awardStore.activeAward.settings;
  return formatPhone(smsNumber);
});

function verifySmsOrCallOrEmail(desiredChannel: Channel) {
  errorMsg.value = "";
  channel.value = desiredChannel;
  const to = ["sms", "call"].includes(desiredChannel)
    ? selectedPhone.value
    : props.email;
  sending.value = true;
  twilioStore
    .startVerify({
      to,
      channel: channel.value,
      entityKey: { PK: `CANDIDATE#${props.candidate.id}`, SK: "METADATA" },
    })
    .then(({ success, message = "", status }) => {
      codeSent.value = success;
      if (status !== 429) {
        errorMsg.value = message;
      }
    })
    .finally(() => {
      sending.value = false;
      setTimeout(() => {
        showNotWorking.value = true;
      }, 120000);
    });
}

function showManualClaim() {
  showManualClaimForm.value = true;
}

async function submitManualClaim() {
  verificationIssueMissing.value = !verificationIssue.value;
  verificationIssueOtherMissing.value =
    verificationIssue.value === "other" && !verificationIssueOther.value;
  if (verificationIssueMissing.value || verificationIssueOtherMissing.value) {
    return;
  }
  submittingManualClaim.value = true;
  try {
    await candidatesStore.submitManualClaim(props.candidate, {
      verificationIssue: verificationIssue.value,
      verificationIssueOther: verificationIssueOther.value,
      ...props.formData,
      awardYear: afterCutoff.value ? awardYear.value + 1 : awardYear.value,
    });
    props.onVerified({ manual: true });
    close();
  } catch (error) {
    console.error(error);
  } finally {
    submittingManualClaim.value = false;
  }
}

function reset() {
  codeSent.value = false;
  errorMsg.value = "";
  code.value = "";
  selectedPhone.value = props.phones[0] ?? "";
  verifiedPhones.value = [];
  waitingForInboundCall.value = false;
  clearInterval(refreshIntervalId.value);
  refreshIntervalId.value = -1;
  channel.value = "";
  verifying.value = false;
}

function startInboundCall() {
  channel.value = "inbound";
  refreshIntervalId.value = window.setInterval(() => {
    verifyInboundCall();
  }, 1000 * 5);
}

function verifyInboundCall() {
  errorMsg.value = "";
  twilioStore
    .verifyInboundCall({
      phones: props.phones,
      candidateId: props.candidate.id,
    })
    .then(({ verifiedPhones = [], message = "" }) => {
      const verified = verifiedPhones.length > 0;
      errorMsg.value = message;
      if (verified) {
        clearInterval(refreshIntervalId.value);
        props.onVerified({ manual: false });
        close();
      }
    });
}

async function verifyCode() {
  if (!code.value) {
    return;
  }
  try {
    verifying.value = true;
    const to = ["sms", "call"].includes(channel.value)
      ? selectedPhone.value
      : props.email;
    const { verified, message = "" } = await twilioStore.checkVerify({
      to,
      channel: channel.value,
      code: code.value,
      candidateId: props.candidate.id,
      entityKey: { PK: `CANDIDATE#${props.candidate.id}`, SK: "METADATA" },
    });
    if (verified) {
      props.onVerified({ manual: false });
      close();
      return;
    }
    errorMsg.value = !verified ? "Invalid verification code" : message;
  } catch (error) {
    console.error(error);
  } finally {
    verifying.value = false;
  }
}

function close() {
  dialogState.value = false;
}

onBeforeUnmount(() => {
  clearInterval(refreshIntervalId.value);
});
</script>

<template>
  <GDialog
    v-model="dialogState"
    :max-width="'44rem'"
    content-class="rounded-lg shadow-lg bg-white"
  >
    <div
      class="flex flex-col gap-y-4 p-4"
      :class="awardStore.activeAward?.table"
    >
      <h2>Verify Candidate Ownership</h2>
      <div
        v-if="
          !showManualClaimForm && ['sms', 'call', 'email'].includes(channel)
        "
        class="flex flex-col gap-y-2"
      >
        <p>
          You should receive
          <span v-if="channel === 'sms'">a text message</span
          ><span v-if="channel === 'call'">a phone call</span
          ><span v-if="channel === 'email'">an email</span> with your
          verification code. Please enter it below.
        </p>
        <div class="flex flex-col items-center gap-y-2">
          <div
            class="text-center"
            :class="{ visible: !!errorMsg, invisible: !errorMsg }"
          >
            <p class="text-link-red">{{ errorMsg }}</p>
          </div>
          <CodeInput v-model="code" class="mb-2" />
          <Button @click="verifyCode" :disabled="code.length !== 6" class="">
            {{ verifying ? "Verifying..." : "Verify" }}
          </Button>
          <Button
            color="transparent"
            :border="false"
            @click="verifySmsOrCallOrEmail(channel)"
          >
            <span v-if="sending">Sending...</span
            ><span v-else-if="channel === 'email' || channel === 'sms'"
              >Resend Code</span
            >
            <span v-else-if="channel === 'call'">Call Me Again</span>
          </Button>
          <p>
            Please wait up to one minute to receive your code.
            <span v-if="channel === 'email'"
              >Be aware it may end up in a spam folder.</span
            >
          </p>
          <p v-if="channel === 'email' && showNotWorking">
            <Button
              color="transparent"
              :border="false"
              @click="() => showManualClaim()"
              >Not working? Click here.</Button
            >
          </p>
        </div>
      </div>
      <div v-if="channel === 'inbound'">
        <div class="py-2">
          <p>
            Please call <span class="font-bold">{{ verifyPhone }}</span> from
            one of your business numbers:
          </p>
          <ul class="list-disc pl-5">
            <li v-for="phone in phones" :key="phone" class="font-bold">
              {{ formatPhone(phone) }}
            </li>
          </ul>
        </div>
      </div>
      <div v-if="!channel && !showManualClaimForm">
        <div class="">
          We need to verify that you own or represent
          <span class="font-bold">{{ candidate.name }}</span
          >.
          <div v-if="phones.length === 1">
            <p>
              Using your business phone number
              <span class="font-bold">{{ formatPhone(phones[0]) }}</span
              >, you can:
            </p>
          </div>
        </div>
        <div v-if="phones.length > 1">
          <p>
            Please choose one of your business phone numbers to use for
            verification.
          </p>
          <ul>
            <li v-for="phone in phones" :key="phone">
              <RadioInput
                class="py-1 font-medium"
                :name="`phone-${phone}`"
                v-model="selectedPhone"
                :required="true"
                :label="formatPhone(phone)"
                :value="phone"
              />
            </li>
          </ul>
        </div>

        <div v-if="errorMsg" class="text-link-red">
          <p>We weren't able to contact your phone. {{ errorMsg }}.</p>
          <p>You can try the following:</p>
          <ul class="list-disc pl-5">
            <li v-if="phones.length > 1">Select a different phone number.</li>
            <li>Wait about 15 minutes and try again.</li>
          </ul>
        </div>
      </div>

      <div v-if="channel && channel !== 'email'" class="flex justify-center">
        <Button color="transparent" :border="false" @click="reset">
          Try a different method
        </Button>
      </div>
      <div>
        <div v-if="phones.length && !channel" class="flex flex-col gap-y-4">
          <div
            class="flex flex-col items-center justify-center gap-x-2 gap-y-2 sm:flex-row"
            v-if="!showManualClaimForm"
          >
            <Button @click="startInboundCall" class="w-52"> Call Us </Button>
            <Button class="w-52" @click="verifySmsOrCallOrEmail('call')">
              Receive a Phone Call
            </Button>
            <Button class="w-52" @click="verifySmsOrCallOrEmail('sms')">
              Receive a Text Message
            </Button>
          </div>
          <div>
            <p v-if="!showManualClaimForm">
              If none of these options are possible for you,
              <button
                class="font-medium text-link-blue"
                @click="
                  () => {
                    showManualClaimForm = true;
                  }
                "
              >
                click here.
              </button>
            </p>
          </div>
        </div>
        <div v-if="showManualClaimForm" class="flex flex-col gap-y-4">
          <p>
            Please let us know the type of trouble you are having so we can get
            you verified!
          </p>
          <SelectInput
            id="manual-reason"
            name="Reason"
            label="What is the issue?"
            placeholder="Please select an option..."
            v-model="verificationIssue"
            :error="verificationIssueMissing"
            @change="() => (verificationIssueMissing = false)"
            :options="[
              {
                label: 'Incorrect phone number',
                value: 'Incorrect phone number',
              },
              { label: 'I\'m trying to vote', value: 'I\'m trying to vote' },
              {
                label: 'I\'m trying to nominate a company',
                value: 'I\'m trying to nominate a company',
              },
              {
                label: 'Not currently in the office',
                value: 'Not currently in the office',
              },
              {
                label: 'I\'m unavailable for a period of time',
                value: 'I\'m unavailable for a period of time',
              },
              {
                label: 'I cannot receive a voice call or text',
                value: 'I cannot receive a voice call or text',
              },
              {
                label: 'I cannot call out on the phone number listed',
                value: 'I cannot call out on the phone number listed',
              },
              {
                label: 'Other...',
                value: 'other',
              },
            ]"
          />
          <TextArea
            v-if="verificationIssue === 'other'"
            placeholder="Please explain what the issue is"
            v-model="verificationIssueOther"
            :error="verificationIssueOtherMissing"
          ></TextArea>
          <div class="flex flex-col items-center gap-y-2">
            <Button @click="submitManualClaim">{{
              submittingManualClaim ? "Submitting..." : "Submit"
            }}</Button>
            <Button
              color="transparent"
              @click="
                () => {
                  showManualClaimForm = false;
                }
              "
              >Cancel</Button
            >
          </div>
        </div>
        <div v-if="!phones.length">
          <Button class="btn link-blue" @click="close"> OK </Button>
        </div>
      </div>
    </div>
  </GDialog>
</template>
